import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { apiService } from '../services/api';
import SimpleLocationSelector from '../components/SimpleLocationSelector';
import { facebookPixel } from '../utils/facebookPixel';
import { 
  MapPinIcon, 
  PlusIcon, 
  CheckCircleIcon,
  CameraIcon,
  XMarkIcon,
  CreditCardIcon,
  TruckIcon,
  ClockIcon,
  PencilIcon
} from '@heroicons/react/24/outline';

interface Address {
  id: number;
  name: string;
  phone?: string;
  company?: string;
  address_line_1: string;
  address_line_2?: string;
  township: string;
  state_region: string;
  postal_code?: string;
  country: string;
  is_default: boolean;
  formatted_address: string;
}

interface DeliveryFee {
  delivery_fee: number;
  estimated_days: number;
  township_delivery_id: number;
  formatted_price: string;
}

interface OrderTotals {
  subtotal: number;
  delivery_fee: number;
  discount_amount: number;
  total: number;
  order_items: any[];
  township_delivery: any;
  address: Address;
  coupon?: {
    id: number;
    code: string;
    name: string;
    discount_type: string;
    discount_value: number;
    discount_amount: number;
    discount_display: string;
  };
}

const Checkout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { items: cartItems, clearCart } = useCart();
  
  // Get selected items from navigation state, fallback to all cart items
  const selectedItemKeys = location.state?.selectedItems || cartItems.map(item => item.key);
  const checkoutItems = cartItems.filter(item => selectedItemKeys.includes(item.key));
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<Address>>({});
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [deliveryFee, setDeliveryFee] = useState<DeliveryFee | null>(null);
  const [orderTotals, setOrderTotals] = useState<OrderTotals | null>(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showPaymentUpload, setShowPaymentUpload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [paymentScreenshot, setPaymentScreenshot] = useState<File | null>(null);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<'cod' | 'manual_payment'>('manual_payment');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSelectingAddress, setIsSelectingAddress] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<any>(null);
  const [couponError, setCouponError] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [showPaymentQR, setShowPaymentQR] = useState<string | null>(null);

  // New address form state
  const [newAddress, setNewAddress] = useState({
    name: '',
    phone: '',
    company: '',
    address_line_1: '',
    address_line_2: '',
    township: '',
    state_region: '',
    postal_code: '',
    country: 'Myanmar',
    is_default: false
  });

  const loadAddresses = useCallback(async () => {
    try {
      // Clear existing addresses first to prevent stale data
      setAddresses([]);
      setSelectedAddress(null);
      setDeliveryFee(null);
      
      const response = await apiService.get('/addresses');
      let addressesData = response.data || response || [];
      
      // If response is an array directly, use it
      if (Array.isArray(response) && !(response as any).data) {
        addressesData = response;
      }
      
      setAddresses(addressesData);
      
      // Auto-select default address
      if (Array.isArray(addressesData) && addressesData.length > 0) {
        const defaultAddress = addressesData.find((addr: Address) => addr.is_default);
        if (defaultAddress) {
          setSelectedAddress(defaultAddress);
          // Delivery fee will be calculated by the useEffect when selectedAddress changes
        } else {
          // If no default address, don't auto-select any address
          // User must explicitly select an address
          setSelectedAddress(null);
        }
      }
    } catch (error) {
      setError('Failed to load addresses');
    }
  }, []);

  useEffect(() => {
    // Don't redirect if cart is empty
    if (cartItems.length === 0) {
      navigate('/cart');
      return;
    }
    
    // Force clear all address-related state before loading
    setAddresses([]);
    setSelectedAddress(null);
    setDeliveryFee(null);
    setOrderTotals(null);
    
    loadAddresses();
  }, [cartItems, navigate, loadAddresses]);

  // Redirect if no items selected for checkout
  useEffect(() => {
    if (checkoutItems.length === 0) {
      navigate('/cart');
    }
  }, [checkoutItems, navigate]);

  // Removed unused calculateOrderTotals - functionality moved to handleApplyCoupon and calculateDeliveryFee

  const calculateDeliveryFee = useCallback(async (address: Address) => {
    if (!address) return;
    
    setCalculating(true);
    setError(''); // Clear previous errors
    
    try {
      const response = await apiService.calculateDeliveryFee(address.id);
      setDeliveryFee(response);
      
      // Calculate order totals after delivery fee is set
      const couponCode = appliedCoupon?.code;
      const totalsResponse = await apiService.post('/orders/calculate-totals', {
        address_id: address.id,
        cart_items: checkoutItems.map(item => ({
          variant_id: item.variantId,
          size: item.sizeName,
          quantity: item.quantity
        })),
        coupon_code: couponCode || undefined
      });
      
      setOrderTotals(totalsResponse.data);
      
      // Update applied coupon if returned from backend
      if (totalsResponse.data.coupon) {
        setAppliedCoupon(totalsResponse.data.coupon);
        setCouponError('');
      }
    } catch (error: any) {
      setDeliveryFee(null);
      
      // Show more specific error messages
      if (error.response?.status === 401) {
        setError('Authentication failed. Please log in again.');
      } else if (error.response?.status === 404) {
        const debugInfo = error.response?.data?.debug_info;
        setError(`Delivery not available for ${debugInfo?.township || 'this location'}, ${debugInfo?.state_region || 'unknown region'}. Please contact support.`);
      } else {
        setError(error.message || 'Failed to calculate delivery fee. Please try again.');
      }
    } finally {
      setCalculating(false);
    }
  }, [appliedCoupon, checkoutItems]);

  // Address editing handlers
  const handleEditAddress = (address: Address) => {
    setEditingAddress(address);
    setEditFormData(address);
    setShowAddressForm(true);
  };

  const handleEditInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setEditFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleUpdateAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAddress) return;

    setLoading(true);
    try {
      const response = await apiService.put(`/addresses/${editingAddress.id}`, editFormData);
      // apiService.put already returns response.data, so response IS the updated address
      const updatedAddress = response?.data || response;
      
      // Refresh addresses list to get updated data with formatted_address
      await loadAddresses();
      
      // Update selected address if it was the one being edited
      if (selectedAddress?.id === editingAddress.id && updatedAddress?.id) {
        setSelectedAddress(updatedAddress);
        await calculateDeliveryFee(updatedAddress);
      }
      
      // Close form
      setShowAddressForm(false);
      setEditingAddress(null);
      setEditFormData({});
      
      setSuccess('Address updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to update address');
    } finally {
      setLoading(false);
    }
  };

  const cancelEdit = () => {
    setShowAddressForm(false);
    setEditingAddress(null);
    setEditFormData({});
  };

  useEffect(() => {
    if (selectedAddress) {
      // Additional check to ensure this address belongs to current user's loaded addresses
      const isValidAddress = addresses.some(addr => addr.id === selectedAddress.id);
      if (isValidAddress) {
        calculateDeliveryFee(selectedAddress);
      } else {
        setSelectedAddress(null);
      }
    } else {
      // Clear delivery fee when no address is selected
      setDeliveryFee(null);
      setOrderTotals(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddress, addresses]); // Removed calculateDeliveryFee from dependencies to prevent infinite loop

  const handleAddressSelect = (address: Address) => {
    // Prevent double-click by checking if already selecting
    if (isSelectingAddress || calculating) {
      return;
    }

    // Prevent selecting the same address again
    if (selectedAddress?.id === address.id) {
      return;
    }

    setIsSelectingAddress(true);
    
    // Clear previous calculations
    setDeliveryFee(null);
    setOrderTotals(null);
    setError(null);
    
    // Set the selected address (this will trigger the useEffect to calculate delivery fee)
    setSelectedAddress(address);
    
    // Reset the selection flag after a short delay
    setTimeout(() => {
      setIsSelectingAddress(false);
    }, 1000);
  };

  const handleAddNewAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await apiService.post('/addresses', newAddress);
      const newAddressData = response.data;
      
      // Refresh addresses list to get updated data with formatted_address
      await loadAddresses();
      
      // Select the newly created address
      setSelectedAddress(newAddressData);
      
      // Calculate delivery fee for new address
      await calculateDeliveryFee(newAddressData);
      
      // Close form and reset
      setShowAddressForm(false);
      setNewAddress({
        name: '',
        phone: '',
        company: '',
        address_line_1: '',
        address_line_2: '',
        township: '',
        state_region: '',
        postal_code: '',
        country: 'Myanmar',
        is_default: false
      });
      
      setSuccess('Address added successfully!');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to add address');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      setPaymentScreenshot(file);
      setPreviewUrl(URL.createObjectURL(file));
      setError(null);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    if (!selectedAddress) {
      setCouponError('Please select a shipping address first');
      return;
    }

    setApplyingCoupon(true);
    setCouponError('');

    try {
      // Recalculate totals with coupon
      const response = await apiService.post('/orders/calculate-totals', {
        address_id: selectedAddress.id,
        cart_items: checkoutItems.map(item => ({
          variant_id: item.variantId,
          size: item.sizeName,
          quantity: item.quantity
        })),
        coupon_code: couponCode.trim().toUpperCase()
      });
      
      setOrderTotals(response.data);
      
      // Check if coupon was applied successfully from the response
      if (response.data.coupon) {
        setAppliedCoupon(response.data.coupon);
        setCouponError('');
        setSuccess('Coupon applied successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setCouponError('Invalid or expired coupon code');
        setAppliedCoupon(null);
      }
    } catch (error: any) {
      setCouponError(error.response?.data?.message || 'Failed to apply coupon');
      setAppliedCoupon(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = async () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setCouponError('');
    
    if (selectedAddress) {
      try {
        // Recalculate totals without coupon
        const response = await apiService.post('/orders/calculate-totals', {
          address_id: selectedAddress.id,
          cart_items: checkoutItems.map(item => ({
            variant_id: item.variantId,
            size: item.sizeName,
            quantity: item.quantity
          }))
          // No coupon_code sent
        });
        
        setOrderTotals(response.data);
      } catch (error) {
        setError('Failed to recalculate totals');
      }
    }
  };

  const handlePlaceOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate based on payment method
    if (paymentMethod === 'manual_payment' && !paymentScreenshot) {
      setError('Please upload payment screenshot');
      return;
    }
    
    if (!selectedAddress) {
      setError('Please select a shipping address');
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      
      const cartData = checkoutItems.map(item => ({
        product_id: item.productId,
        variant_id: item.variantId,
        size: item.sizeName,
        quantity: item.quantity,
        unit_price: item.unitPrice
      }));

      formData.append('cart_items', JSON.stringify(cartData));
      formData.append('shipping_address_id', selectedAddress.id.toString());
      formData.append('payment_method', paymentMethod);
      
      // Only add payment screenshot if manual payment is selected
      if (paymentMethod === 'manual_payment' && paymentScreenshot) {
        formData.append('payment_screenshot', paymentScreenshot);
      }
      
      if (paymentNotes) {
        formData.append('payment_notes', paymentNotes);
      }
      if (appliedCoupon?.code) {
        formData.append('coupon_code', appliedCoupon.code);
      }

      const response = await apiService.post('/checkout/place-order', formData);

      // Track Facebook Pixel Purchase event
      const totalValue = checkoutItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
      facebookPixel.trackPurchase({
        content_ids: checkoutItems.map(item => item.productId.toString()),
        content_type: 'product',
        value: totalValue,
        currency: 'MMK',
        num_items: checkoutItems.reduce((sum, item) => sum + item.quantity, 0),
        order_id: response.data.order_number
      });

      // Redirect to order success page
      navigate(`/order-success?order=${response.data.order_number}`);
      clearCart(); // Clear cart after successful order

    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  if (checkoutItems.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Progress Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-center">
          <div className="flex items-center w-full max-w-2xl">
            <button 
              onClick={() => navigate('/cart')}
              className="flex items-center flex-shrink-0 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <div className="flex items-center justify-center w-8 h-8 bg-gray-500 text-white rounded-full text-sm font-medium">
                1
              </div>
              <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-medium text-gray-900 whitespace-nowrap">Cart</span>
            </button>
            <div className="flex-1 h-px bg-gray-300 mx-2 sm:mx-4"></div>
            <div className="flex items-center flex-shrink-0">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full text-sm font-medium">
                2
              </div>
              <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-medium text-blue-600 whitespace-nowrap">Checkout</span>
            </div>
            <div className="flex-1 h-px bg-gray-300 mx-2 sm:mx-4"></div>
            <div className="flex items-center flex-shrink-0">
              <div className="flex items-center justify-center w-8 h-8 bg-gray-300 text-gray-500 rounded-full text-sm font-medium">
                3
              </div>
              <span className="ml-1 sm:ml-2 text-xs sm:text-sm font-medium text-gray-500 whitespace-nowrap">Complete</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Secure Checkout</h1>
          <div className="flex items-center mt-2 text-sm text-gray-600">
            <svg className="w-4 h-4 mr-1 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
            </svg>
            SSL Secured • Your information is protected
          </div>
        </div>

        {/* Toast Notifications */}
        {error && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg shadow-sm animate-fade-in">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg shadow-sm animate-fade-in">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-800">{success}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col lg:grid lg:grid-cols-3 gap-8">
          {/* Left Column - Address & Payment */}
          <div className="lg:col-span-2 space-y-8 order-2 lg:order-1">
            {/* Shipping Address Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 transition-all duration-200 ease-in-out hover:shadow-md">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900 flex items-center">
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full mr-3">
                    <MapPinIcon className="h-5 w-5 text-gray-600" />
                  </div>
                  Shipping Address
                </h2>
                <span className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full">Step 1 of 2</span>
              </div>

              {/* Address Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Existing Addresses */}
                {addresses && addresses.length > 0 && addresses.filter(address => address && address.id).map((address) => (
                  <div
                    key={address.id}
                    className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all duration-200 ease-in-out group ${
                      selectedAddress?.id === address.id
                        ? 'border-gray-500 bg-gray-50 shadow-lg ring-4 ring-gray-100'
                        : 'border-gray-200 hover:border-gray-400 hover:shadow-md hover:bg-gray-50'
                    } ${(isSelectingAddress || calculating) ? 'pointer-events-none opacity-75' : ''}`}
                    onClick={() => handleAddressSelect(address)}
                  >
                    {selectedAddress?.id === address.id && (
                      <div className="absolute -top-2 -right-2 bg-gray-600 text-white rounded-full p-1.5 shadow-lg">
                        <CheckCircleIcon className="h-4 w-4" />
                      </div>
                    )}
                    
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 text-lg mb-1">{address.name}</h3>
                        {address.is_default && (
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            Default Address
                          </span>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditAddress(address);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200 ease-in-out flex-shrink-0"
                        title="Edit address"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="text-gray-600 space-y-2">
                      {address.phone && (
                        <div className="flex items-center text-sm">
                          <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          {address.phone}
                        </div>
                      )}
                      <div className="flex items-start text-sm">
                        <svg className="w-4 h-4 mr-2 mt-0.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="leading-relaxed">{address.formatted_address}</span>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Add New Address Card */}
                <div
                  className="border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer transition-all duration-200 ease-in-out hover:border-gray-400 hover:bg-gray-50 hover:shadow-md flex flex-col items-center justify-center min-h-[180px] group"
                  onClick={() => setShowAddressForm(true)}
                >
                  <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-3 group-hover:bg-gray-200 transition-colors duration-200">
                    <PlusIcon className="h-6 w-6 text-gray-500 group-hover:text-gray-600" />
                  </div>
                  <span className="text-gray-700 font-semibold text-lg mb-1">Add New Address</span>
                  <span className="text-gray-500 text-sm text-center leading-relaxed">Click to add a new<br />shipping address</span>
                </div>
              </div>

              {/* Add New Address Form */}
              {showAddressForm && (
                <div className="mt-8 bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-bold text-gray-900">
                      {editingAddress ? 'Edit Address' : 'Add New Address'}
                    </h3>
                    <button
                      type="button"
                      onClick={editingAddress ? cancelEdit : () => setShowAddressForm(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <form onSubmit={editingAddress ? handleUpdateAddress : handleAddNewAddress} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          required
                          name="name"
                          value={editingAddress ? (editFormData.name || '') : newAddress.name}
                          onChange={editingAddress ? handleEditInputChange : (e) => setNewAddress(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={editingAddress ? (editFormData.phone || '') : newAddress.phone}
                          onChange={editingAddress ? handleEditInputChange : (e) => setNewAddress(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Company (Optional)
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={editingAddress ? (editFormData.company || '') : newAddress.company}
                        onChange={editingAddress ? handleEditInputChange : (e) => setNewAddress(prev => ({ ...prev, company: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Address Line 1 *
                      </label>
                      <input
                        type="text"
                        required
                        name="address_line_1"
                        value={editingAddress ? (editFormData.address_line_1 || '') : newAddress.address_line_1}
                        onChange={editingAddress ? handleEditInputChange : (e) => setNewAddress(prev => ({ ...prev, address_line_1: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Address Line 2 (Optional)
                      </label>
                      <input
                        type="text"
                        name="address_line_2"
                        value={editingAddress ? (editFormData.address_line_2 || '') : newAddress.address_line_2}
                        onChange={editingAddress ? handleEditInputChange : (e) => setNewAddress(prev => ({ ...prev, address_line_2: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="md:col-span-1">
                        <SimpleLocationSelector
                          selectedStateRegion={editingAddress ? (editFormData.state_region || '') : newAddress.state_region}
                          selectedTownship={editingAddress ? (editFormData.township || '') : newAddress.township}
                          onStateRegionChange={(value: string) => {
                            if (editingAddress) {
                              setEditFormData(prev => ({ ...prev, state_region: value }));
                            } else {
                              setNewAddress(prev => ({ ...prev, state_region: value }));
                            }
                          }}
                          onTownshipChange={(value: string) => {
                            if (editingAddress) {
                              setEditFormData(prev => ({ ...prev, township: value }));
                            } else {
                              setNewAddress(prev => ({ ...prev, township: value }));
                            }
                          }}
                          disabled={loading}
                          showDeliveryInfo={true}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Postal Code
                        </label>
                        <input
                          type="text"
                          name="postal_code"
                          value={editingAddress ? (editFormData.postal_code || '') : newAddress.postal_code}
                          onChange={editingAddress ? handleEditInputChange : (e) => setNewAddress(prev => ({ ...prev, postal_code: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Country *
                      </label>
                      <input
                        type="text"
                        required
                        name="country"
                        value={editingAddress ? (editFormData.country || '') : newAddress.country}
                        onChange={editingAddress ? handleEditInputChange : (e) => setNewAddress(prev => ({ ...prev, country: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md active:scale-[0.998]"
                      />
                    </div>

                    <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={editingAddress ? cancelEdit : () => setShowAddressForm(false)}
                        className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium transition-all duration-200 ease-in-out hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:ring-4 focus:ring-gray-100 active:scale-[0.998]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-3 bg-gray-600 text-white rounded-lg font-medium transition-all duration-200 ease-in-out hover:bg-gray-700 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-gray-100 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.998]"
                      >
                        {loading ? (
                          <div className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            {editingAddress ? 'Updating...' : 'Adding...'}
                          </div>
                        ) : (
                          editingAddress ? 'Update Address' : 'Add Address'
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>

            {/* Payment Method Section */}
            {selectedAddress && !calculating && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 transition-all duration-200 ease-in-out hover:shadow-md">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center">
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full mr-3">
                      <CreditCardIcon className="h-5 w-5 text-gray-600" />
                    </div>
                    Payment Method
                  </h2>
                  <span className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full">Step 2 of 2</span>
                </div>

                {/* Payment Method Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  {/* Cash on Delivery Option */}
                  <div
                    onClick={() => setPaymentMethod('cod')}
                    className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                      paymentMethod === 'cod'
                        ? 'border-gray-600 bg-gray-50 shadow-lg ring-4 ring-gray-100'
                        : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full mr-4 flex-shrink-0 ${
                        paymentMethod === 'cod' ? 'bg-gray-600' : 'bg-gray-100'
                      }`}>
                        <TruckIcon className={`h-5 w-5 ${paymentMethod === 'cod' ? 'text-white' : 'text-gray-600'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 mb-2">Cash on Delivery</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Pay with cash when your order is delivered to your doorstep.
                        </p>
                      </div>
                      {paymentMethod === 'cod' && (
                        <CheckCircleIcon className="h-6 w-6 text-gray-600 flex-shrink-0" />
                      )}
                    </div>
                  </div>

                  {/* Bank Transfer Option */}
                  <div
                    onClick={() => setPaymentMethod('manual_payment')}
                    className={`cursor-pointer border-2 rounded-xl p-6 transition-all duration-200 ${
                      paymentMethod === 'manual_payment'
                        ? 'border-gray-600 bg-gray-50 shadow-lg ring-4 ring-gray-100'
                        : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-start">
                      <div className={`flex items-center justify-center w-10 h-10 rounded-full mr-4 flex-shrink-0 ${
                        paymentMethod === 'manual_payment' ? 'bg-gray-600' : 'bg-gray-100'
                      }`}>
                        <CreditCardIcon className={`h-5 w-5 ${paymentMethod === 'manual_payment' ? 'text-white' : 'text-gray-600'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 mb-2">Bank Transfer</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          Transfer to our bank account and upload payment screenshot.
                        </p>
                      </div>
                      {paymentMethod === 'manual_payment' && (
                        <CheckCircleIcon className="h-6 w-6 text-gray-600 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                </div>

                {/* COD Information */}
                {paymentMethod === 'cod' && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8">
                    <div className="flex items-start">
                      <div className="flex items-center justify-center w-10 h-10 bg-gray-600 rounded-full mr-4 flex-shrink-0">
                        <TruckIcon className="h-5 w-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 mb-2">Cash on Delivery</h3>
                        <p className="text-gray-700 text-sm leading-relaxed mb-4">
                          Pay with cash when your order arrives. Please have the exact amount ready for the delivery person.
                        </p>
                        <div className="flex items-center text-xs text-gray-500">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          No advance payment required
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bank Transfer Information */}
                {paymentMethod === 'manual_payment' && (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 mb-8">
                    <div className="flex items-start">
                      <div className="flex items-center justify-center w-10 h-10 bg-gray-600 rounded-full mr-4 flex-shrink-0">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-gray-900 mb-2">Bank Transfer Payment</h3>
                        <p className="text-gray-700 text-sm leading-relaxed mb-4">
                          Transfer the total amount to our bank account and upload your transaction screenshot.
                          Your order will be processed after payment verification.
                        </p>
                      
                      {/* Supported Payment Methods */}
                      <div className="mb-4">
                        <p className="text-sm font-semibold text-gray-800 mb-3">Supported Payment Methods:</p>
                        <div className="flex flex-wrap items-center gap-4">
                          <img 
                            src="/bank_logo/Kpay.png" 
                            alt="KPay" 
                            className="h-8 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                            onClick={() => setShowPaymentQR('kpay')}
                          />
                          <img 
                            src="/bank_logo/CBpay.png" 
                            alt="CB Pay" 
                            className="h-8 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                            onClick={() => setShowPaymentQR('cbpay')}
                          />
                          <img 
                            src="/bank_logo/OK$.png" 
                            alt="OK$" 
                            className="h-8 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                            onClick={() => setShowPaymentQR('ok')}
                          />
                          <img 
                            src="/bank_logo/UABpay.png" 
                            alt="UAB Pay" 
                            className="h-8 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                            onClick={() => setShowPaymentQR('uabpay')}
                          />
                          <img 
                            src="/bank_logo/Waypay.png" 
                            alt="Wave Pay" 
                            className="h-8 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                            onClick={() => setShowPaymentQR('wavepay')}
                          />
                          <img 
                            src="/bank_logo/ayapay.jpg" 
                            alt="AYA Pay" 
                            className="h-8 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                            onClick={() => setShowPaymentQR('ayapay')}
                          />
                          <img 
                            src="/bank_logo/Yoma.png" 
                            alt="Yoma Bank" 
                            className="h-8 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                            onClick={() => setShowPaymentQR('yoma')}
                          />
                        </div>
                      </div>
                      
                      <div className="flex items-center text-xs text-gray-500">
                        <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        Secure payment processing
                      </div>
                    </div>
                  </div>
                  </div>
                )}

                {/* COD - Direct Place Order Button */}
                {paymentMethod === 'cod' && (
                  <form onSubmit={handlePlaceOrder}>
                    <div className="mb-6">
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Order Notes (Optional)
                      </label>
                      <textarea
                        value={paymentNotes}
                        onChange={(e) => setPaymentNotes(e.target.value)}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md resize-none"
                        placeholder="Add any special instructions for delivery..."
                      />
                    </div>
                    
                    <div className="flex justify-end pt-6 border-t border-gray-200">
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-8 py-3 bg-gray-600 text-white rounded-lg font-bold transition-all duration-200 ease-in-out hover:bg-gray-700 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-gray-100 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.998]"
                      >
                        {loading ? (
                          <div className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Placing Order...
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Place Order (COD)
                          </div>
                        )}
                      </button>
                    </div>
                  </form>
                )}

                {/* Bank Transfer - Upload Screenshot */}
                {paymentMethod === 'manual_payment' && !showPaymentUpload && (
                  <button
                    onClick={() => setShowPaymentUpload(true)}
                    className="w-full bg-white border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:bg-gray-50 hover:border-gray-400 hover:shadow-md transition-all duration-200 ease-in-out group"
                  >
                    <div className="flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mx-auto mb-4 group-hover:bg-gray-200 transition-colors duration-200">
                      <CameraIcon className="h-8 w-8 text-gray-500 group-hover:text-gray-600" />
                    </div>
                    <p className="text-gray-700 font-bold text-lg mb-2">Upload Payment Screenshot</p>
                    <p className="text-gray-500 text-sm leading-relaxed">Click to upload your transaction proof<br />Supported formats: JPG, PNG (Max 5MB)</p>
                  </button>
                )}
                
                {paymentMethod === 'manual_payment' && showPaymentUpload && (
                  <form onSubmit={handlePlaceOrder} className="space-y-8">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Payment Screenshot *
                      </label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          className="hidden"
                          id="payment-screenshot"
                          required
                        />
                        <label
                          htmlFor="payment-screenshot"
                          className="cursor-pointer bg-white border border-gray-300 rounded-lg px-6 py-3 text-sm font-medium text-gray-700 transition-all duration-200 ease-in-out hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:ring-4 focus:ring-gray-100 active:scale-[0.998]"
                        >
                          <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          Choose File
                        </label>
                        {paymentScreenshot && (
                          <div className="flex items-center text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                            <svg className="w-4 h-4 mr-2 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            {paymentScreenshot.name}
                          </div>
                        )}
                      </div>
                      
                      {previewUrl && (
                        <div className="mt-6 relative inline-block">
                          <div className="relative bg-gray-50 rounded-xl p-4 border border-gray-200">
                            <img
                              src={previewUrl}
                              alt="Payment screenshot preview"
                              className="h-40 w-auto rounded-lg border border-gray-300 shadow-sm"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setPaymentScreenshot(null);
                                setPreviewUrl(null);
                              }}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors duration-200 shadow-lg"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Preview of your payment screenshot</p>
                        </div>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-3">
                        Payment Notes (Optional)
                      </label>
                      <textarea
                        value={paymentNotes}
                        onChange={(e) => setPaymentNotes(e.target.value)}
                        rows={4}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white transition-all duration-200 ease-in-out hover:border-gray-400 hover:shadow-sm focus:outline-none focus:border-gray-500 focus:ring-4 focus:ring-gray-100 focus:shadow-md resize-none"
                        placeholder="Add any additional notes about your payment (transaction ID, reference number, etc.)..."
                      />
                    </div>

                    <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => {
                          setShowPaymentUpload(false);
                          setPaymentScreenshot(null);
                          setPreviewUrl(null);
                          setPaymentNotes('');
                        }}
                        className="px-6 py-3 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium transition-all duration-200 ease-in-out hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm focus:outline-none focus:ring-4 focus:ring-gray-100 active:scale-[0.998]"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading || !paymentScreenshot}
                        className="px-8 py-3 bg-gray-600 text-white rounded-lg font-bold transition-all duration-200 ease-in-out hover:bg-gray-700 hover:shadow-lg focus:outline-none focus:ring-4 focus:ring-gray-100 disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.998]"
                      >
                        {loading ? (
                          <div className="flex items-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Placing Order...
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Complete Order
                          </div>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1 order-1 lg:order-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 lg:sticky lg:top-8 transition-all duration-200 ease-in-out hover:shadow-md">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Order Summary</h2>
                <div className="flex items-center text-sm text-gray-500">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {cartItems.length} item{cartItems.length !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Cart Items */}
              <div className="space-y-4 mb-8">
                {cartItems && cartItems.length > 0 ? cartItems.map((item) => (
                  <div key={`${item.variantId}-${item.sizeName}`} className="flex items-start space-x-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="h-20 w-20 object-cover rounded-lg border border-gray-200 shadow-sm"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-gray-900 mb-2 leading-tight">
                        {item.name}
                      </h3>
                      <div className="flex items-center space-x-2 mb-2">
                        <div className="flex items-center text-xs text-gray-600">
                          <div 
                            className="w-3 h-3 rounded-full border border-gray-300 mr-1" 
                            style={{ backgroundColor: item.colorCode }}
                          ></div>
                          {item.colorName}
                        </div>
                        <span className="text-gray-400">•</span>
                        <span className="text-xs text-gray-600">{item.sizeName}</span>
                        <span className="text-gray-400">•</span>
                        <span className="text-xs text-gray-600">Qty: {item.quantity}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-bold text-gray-900">
                          {(item.unitPrice * item.quantity).toLocaleString("en-US")} Ks
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.unitPrice.toLocaleString("en-US")} Ks each
                        </p>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                    </svg>
                    No items in cart
                  </div>
                )}
              </div>

              {/* Delivery Information */}
              {calculating && (
                <div className="mb-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="animate-pulse">
                    <div className="flex items-center mb-3">
                      <div className="h-5 w-5 bg-gray-300 rounded mr-2"></div>
                      <div className="h-4 bg-gray-300 rounded w-32"></div>
                    </div>
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </div>
              )}

              {deliveryFee && (
                <div className="mb-8 p-6 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="flex items-center text-gray-800 mb-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-600 rounded-full mr-3">
                      <TruckIcon className="h-4 w-4 text-white" />
                    </div>
                    <span className="font-bold">Delivery Information</span>
                  </div>
                  <div className="flex items-center text-gray-700 text-sm">
                    <ClockIcon className="h-4 w-4 mr-2 text-gray-500" />
                    <span>Estimated delivery: <strong>{deliveryFee.estimated_days} days</strong></span>
                  </div>
                </div>
              )}

              {/* Coupon Section */}
              {selectedAddress && !calculating && (
                <div className="bg-white rounded-xl p-6 border border-gray-200 mb-6">
                  <h3 className="font-bold text-gray-900 mb-4">Discount Code</h3>
                  
                  {!appliedCoupon ? (
                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={(e) => {
                            setCouponCode(e.target.value.toUpperCase());
                            setCouponError('');
                          }}
                          placeholder="Enter coupon code"
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-gray-500 focus:ring-2 focus:ring-gray-100 text-sm uppercase"
                          disabled={applyingCoupon}
                        />
                        <button
                          onClick={handleApplyCoupon}
                          disabled={applyingCoupon || !couponCode.trim()}
                          className="px-6 py-2 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm whitespace-nowrap"
                        >
                          {applyingCoupon ? 'Applying...' : 'Apply'}
                        </button>
                      </div>
                      {couponError && (
                        <p className="text-red-600 text-xs">{couponError}</p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <CheckCircleIcon className="h-5 w-5 text-green-600" />
                            <span className="font-bold text-green-900">{appliedCoupon.code}</span>
                          </div>
                          <p className="text-sm text-green-700">
                            {appliedCoupon.name || `${appliedCoupon.discount_display} discount applied`}
                          </p>
                        </div>
                        <button
                          onClick={handleRemoveCoupon}
                          className="text-green-600 hover:text-green-800 p-1"
                          title="Remove coupon"
                        >
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Order Totals */}
              {(orderTotals || deliveryFee) && (
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-4">Order Total</h3>
                  <div className="space-y-3">
                    {orderTotals ? (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Subtotal</span>
                          <span className="text-gray-900 font-medium">{orderTotals.subtotal.toLocaleString("en-US")} Ks</span>
                        </div>
                        {orderTotals.discount_amount > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Discount</span>
                            <span className="text-green-600 font-medium">-{orderTotals.discount_amount.toLocaleString("en-US")} Ks</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Delivery Fee</span>
                          <span className="text-gray-900 font-medium">
                            {orderTotals.delivery_fee > 0 
                              ? `${orderTotals.delivery_fee.toLocaleString("en-US")} Ks`
                              : <span className="text-green-600 font-bold">Free</span>
                            }
                          </span>
                        </div>
                        <div className="border-t border-gray-300 pt-3">
                          <div className="flex justify-between text-lg font-bold">
                            <span className="text-gray-900">Total Amount</span>
                            <span className="text-gray-900">{orderTotals.total.toLocaleString("en-US")} Ks</span>
                          </div>
                        </div>
                      </>
                    ) : deliveryFee && (
                      <>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Subtotal</span>
                          <span className="text-gray-900 font-medium">
                            {cartItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0).toLocaleString("en-US")} Ks
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Delivery Fee</span>
                          <span className="text-gray-900 font-medium">
                            {deliveryFee.delivery_fee > 0 
                              ? `${deliveryFee.delivery_fee.toLocaleString("en-US")} Ks`
                              : <span className="text-green-600 font-bold">Free</span>
                            }
                          </span>
                        </div>
                        <div className="border-t border-gray-300 pt-3">
                          <div className="flex justify-between text-lg font-bold">
                            <span className="text-gray-900">Total Amount</span>
                            <span className="text-gray-900">
                              {(cartItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0) + deliveryFee.delivery_fee).toLocaleString("en-US")} Ks
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Fallback when no delivery fee but address selected */}
              {selectedAddress && !calculating && !orderTotals && !deliveryFee && (
                <div className="mt-6 p-6 bg-orange-50 border border-orange-200 rounded-xl">
                  <div className="flex items-start">
                    <div className="flex items-center justify-center w-8 h-8 bg-orange-500 rounded-full mr-3 flex-shrink-0">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-orange-900 mb-1">Delivery Unavailable</h4>
                      <p className="text-orange-800 text-sm leading-relaxed">
                        Delivery may not be available for this location. Please contact support or try a different address.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {!selectedAddress && (
                <div className="mt-6 p-6 bg-gray-50 border border-gray-200 rounded-xl">
                  <div className="flex items-start">
                    <div className="flex items-center justify-center w-8 h-8 bg-gray-500 rounded-full mr-3 flex-shrink-0">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Select Shipping Address</h4>
                      <p className="text-gray-700 text-sm leading-relaxed">
                        Please select a shipping address to see delivery options and calculate your total cost.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Trust Indicators */}
        <div className="mt-12 text-center">
          <div className="flex items-center justify-center space-x-8 text-gray-500 text-sm">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              SSL Encrypted
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Secure Payment
            </div>
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              24/7 Support
            </div>
          </div>
        </div>
      </div>

      {/* Payment QR Code Modal */}
      {showPaymentQR && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={() => setShowPaymentQR(null)}
        >
          <div 
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative shadow-2xl transform transition-all duration-300 scale-100 animate-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with gradient background */}
            <div className={`${
              showPaymentQR === 'kpay' 
                ? 'bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800' 
                : showPaymentQR === 'ayapay'
                ? 'bg-gradient-to-br from-red-600 via-red-700 to-red-800'
                : showPaymentQR === 'cbpay'
                ? 'bg-gradient-to-br from-[#2b3b96] via-[#232f7a] to-[#1b2560]'
                : showPaymentQR === 'wavepay'
                ? 'bg-gradient-to-br from-yellow-500 via-yellow-600 to-yellow-700'
                : showPaymentQR === 'ok'
                ? 'bg-gradient-to-br from-[#f8cf3f] via-[#e6bd2d] to-[#d4ab1b]'
                : showPaymentQR === 'yoma'
                ? 'bg-gradient-to-br from-[#f30023] via-[#d10020] to-[#b0001c]'
                : 'bg-gradient-to-br from-[#2596be] via-[#1e7a9e] to-[#176080]'
            } rounded-t-3xl p-6 text-center relative overflow-hidden`}>
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16 pointer-events-none"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12 pointer-events-none"></div>
              
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowPaymentQR(null);
                }}
                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full z-20"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
              
              <div className="flex flex-col items-center relative z-10">
                <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-4 shadow-xl ring-4 ring-white/20">
                  <img 
                    src={showPaymentQR === 'kpay' ? '/bank_logo/Kpay.png' : showPaymentQR === 'ayapay' ? '/bank_logo/ayapay.jpg' : showPaymentQR === 'cbpay' ? '/bank_logo/CBpay.png' : showPaymentQR === 'wavepay' ? '/bank_logo/Waypay.png' : showPaymentQR === 'ok' ? '/bank_logo/OK$.png' : showPaymentQR === 'yoma' ? '/bank_logo/Yoma.png' : '/bank_logo/UABpay.png'}
                    alt={showPaymentQR === 'kpay' ? 'KPay' : showPaymentQR === 'ayapay' ? 'AYA Pay' : showPaymentQR === 'cbpay' ? 'CB Pay' : showPaymentQR === 'wavepay' ? 'Wave Pay' : showPaymentQR === 'ok' ? 'OK$' : showPaymentQR === 'yoma' ? 'Yoma Bank' : 'UAB Pay'}
                    className="h-10"
                  />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {showPaymentQR === 'kpay' ? 'KPay Payment' : showPaymentQR === 'ayapay' ? 'AYA Payment' : showPaymentQR === 'cbpay' ? 'CB Payment' : showPaymentQR === 'wavepay' ? 'Wave Pay Payment' : showPaymentQR === 'ok' ? 'OK$ Payment' : showPaymentQR === 'yoma' ? 'Yoma Bank Payment' : 'UAB Pay Payment'}
                </h3>
                <p className={`${
                  showPaymentQR === 'kpay' ? 'text-blue-100' : showPaymentQR === 'ayapay' ? 'text-red-100' : showPaymentQR === 'cbpay' ? 'text-[#d4ddf7]' : showPaymentQR === 'wavepay' ? 'text-yellow-100' : showPaymentQR === 'ok' ? 'text-[#fef9e7]' : showPaymentQR === 'yoma' ? 'text-[#ffd9df]' : 'text-blue-100'
                } text-sm font-medium`}>
                  {showPaymentQR === 'ok' || showPaymentQR === 'yoma' ? 'Transfer to this account' : 'Scan QR code to complete your payment'}
                </p>
              </div>
            </div>
            
            {/* Content Section */}
            <div className="p-6">
              {showPaymentQR === 'yoma' ? (
                /* Yoma Bank Account Display */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Account Number Section */}
                  <div className="flex flex-col items-center justify-center">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 mb-3 border border-gray-200 w-full">
                      <div className="bg-white rounded-xl p-6 shadow-sm flex flex-col items-center justify-center min-h-[180px]">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Account Name:</p>
                        <p className="text-sm font-bold text-gray-900 mb-3 text-center">Aung Thu</p>
                        <p className="text-sm font-semibold text-gray-900 mb-3 text-center">Yoma Bank Flexi Everyday Account</p>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Account Number:</p>
                        <p className="text-xl md:text-2xl font-bold text-gray-900 tracking-wide mb-1">002711190000160</p>
                        <div className="w-16 h-1 bg-[#f30023] rounded-full mt-2"></div>
                      </div>
                    </div>
                    <p className="text-center text-sm text-gray-600 font-medium">
                      Yoma Bank Account
                    </p>
                  </div>
                  
                  {/* Instructions Section */}
                  <div className="flex flex-col justify-center">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">How to pay:</h4>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-7 h-7 bg-[#ffd9df] text-[#b0001c] rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Open your banking app</p>
                          <p className="text-xs text-gray-500">Use any banking app or visit bank</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-7 h-7 bg-[#ffd9df] text-[#b0001c] rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Transfer to account</p>
                          <p className="text-xs text-gray-500">Enter 002711190000160 as recipient</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-7 h-7 bg-[#ffd9df] text-[#b0001c] rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Complete payment</p>
                          <p className="text-xs text-gray-500">Confirm the amount and authorize payment</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-7 h-7 bg-[#ffd9df] text-[#b0001c] rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">4</div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Upload screenshot</p>
                          <p className="text-xs text-gray-500">Take a screenshot and upload it above</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : showPaymentQR === 'ok' ? (
                /* OK$ Phone Number Display - Same Layout as QR Modals */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Phone Number Section */}
                  <div className="flex flex-col items-center justify-center">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 mb-3 border border-gray-200 w-full">
                      <div className="bg-white rounded-xl p-6 shadow-sm flex flex-col items-center justify-center min-h-[180px]">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Transfer to:</p>
                        <p className="text-2xl md:text-3xl font-bold text-gray-900 tracking-wide mb-1">09975541930</p>
                        <div className="w-16 h-1 bg-[#f8cf3f] rounded-full mt-2"></div>
                      </div>
                    </div>
                    <p className="text-center text-sm text-gray-600 font-medium">
                      OK$ Account Number
                    </p>
                  </div>
                  
                  {/* Instructions Section */}
                  <div className="flex flex-col justify-center">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">How to pay:</h4>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-7 h-7 bg-[#fef9e7] text-[#d4ab1b] rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Open OK$ app</p>
                          <p className="text-xs text-gray-500">Launch the mobile app on your phone</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-7 h-7 bg-[#fef9e7] text-[#d4ab1b] rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Transfer to phone number</p>
                          <p className="text-xs text-gray-500">Enter 09975541930 as recipient</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-7 h-7 bg-[#fef9e7] text-[#d4ab1b] rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Complete payment</p>
                          <p className="text-xs text-gray-500">Confirm the amount and authorize payment</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-7 h-7 bg-[#fef9e7] text-[#d4ab1b] rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">4</div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Upload screenshot</p>
                          <p className="text-xs text-gray-500">Take a screenshot and upload it above</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : showPaymentQR === 'kpay' ? (
                /* KPay - Same Layout as Wave Pay */
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Left Column - QR Code */}
                    <div className="flex flex-col items-center">
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 mb-3 border border-gray-200 w-full">
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <img 
                            src="/bank_logo/kpay_qr.JPG"
                            alt="KPay QR Code"
                            className="w-full max-w-[180px] mx-auto rounded-lg"
                          />
                        </div>
                      </div>
                      <p className="text-center text-sm text-gray-600 font-medium">
                        Use KBZPay Scan to pay
                      </p>
                    </div>

                    {/* Right Column - Banking Details */}
                    <div className="flex flex-col justify-center">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 rounded-xl p-4 shadow-sm">
                        <div className="flex items-center justify-center mb-3">
                          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 py-1 rounded-full shadow-md">
                            <svg className="w-3 h-3 inline mr-1.5 -mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                              <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                            </svg>
                            <span className="text-xs font-bold uppercase tracking-wide">BANKING DETAILS</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {/* Account Name */}
                          <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-blue-500">
                            <div className="flex items-center mb-1">
                              <svg className="w-3.5 h-3.5 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                              </svg>
                              <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Account Name</span>
                            </div>
                            <p className="text-sm text-gray-900 font-bold ml-5">Aung Thu</p>
                          </div>

                          {/* Bank Name */}
                          <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-blue-500">
                            <div className="flex items-center mb-1">
                              <svg className="w-3.5 h-3.5 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-5L9 4H4zm7 5a1 1 0 10-2 0v1H8a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V9z" clipRule="evenodd"/>
                              </svg>
                              <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Bank</span>
                            </div>
                            <p className="text-sm text-gray-900 font-bold ml-5">KBZ Bank</p>
                          </div>

                          {/* Account Number */}
                          <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-blue-600">
                            <div className="flex items-center mb-1">
                              <svg className="w-3.5 h-3.5 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                              </svg>
                              <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Account Number</span>
                            </div>
                            <p className="text-sm text-gray-900 font-bold tracking-wide ml-5 font-mono">02730102705302501</p>
                          </div>

                          {/* KBZPay Number */}
                          <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-blue-600">
                            <div className="flex items-center mb-1">
                              <svg className="w-3.5 h-3.5 text-blue-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                              </svg>
                              <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">KBZPay Number</span>
                            </div>
                            <p className="text-sm text-gray-900 font-bold ml-5">09795424337</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Instructions Section - Full Width Below */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">How to pay:</h4>
                    <div className="space-y-3">
                      <div className="flex items-start space-x-3">
                        <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-md">1</div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">Scan QR or use account details</p>
                          <p className="text-xs text-gray-600">Open KBZPay app and scan QR code, or manually transfer using the account details</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-md">2</div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">Complete payment</p>
                          <p className="text-xs text-gray-600">Confirm the amount and authorize the payment</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-md">3</div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">Upload screenshot</p>
                          <p className="text-xs text-gray-600">Take a screenshot of the transaction and upload it above</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* QR Code and Details - Other Payment Methods */
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* QR Code Section */}
                    <div className="flex flex-col items-center">
                      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 mb-3 border border-gray-200 w-full">
                        <div className="bg-white rounded-xl p-4 shadow-sm">
                          <img 
                            src={showPaymentQR === 'ayapay' ? '/bank_logo/aya_qr.JPG' : showPaymentQR === 'cbpay' ? '/bank_logo/CBpay_qr.JPG' : showPaymentQR === 'wavepay' ? '/bank_logo/wave_qr.PNG' : '/bank_logo/uab_qr.PNG'}
                            alt={`${showPaymentQR === 'ayapay' ? 'AYA Pay' : showPaymentQR === 'cbpay' ? 'CB Pay' : showPaymentQR === 'wavepay' ? 'Wave Pay' : 'UAB Pay'} QR Code`}
                            className="w-full max-w-[180px] mx-auto rounded-lg"
                          />
                        </div>
                      </div>
                      <p className="text-center text-sm text-gray-600 font-medium">
                        Use {showPaymentQR === 'ayapay' ? 'AYA Pay' : showPaymentQR === 'cbpay' ? 'CB Pay' : showPaymentQR === 'wavepay' ? 'Wave Pay' : 'UAB Pay'} Scan to pay
                      </p>
                    </div>
                  
                    {/* Right Column - Payment Details */}
                    <div className="flex flex-col justify-center">
                      {/* Wave Pay Details */}
                      {showPaymentQR === 'wavepay' && (
                        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-200 rounded-xl p-4 shadow-sm">
                          <div className="flex items-center justify-center mb-3">
                            <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white px-3 py-1 rounded-full shadow-md">
                              <svg className="w-3 h-3 inline mr-1.5 -mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                              </svg>
                              <span className="text-xs font-bold uppercase tracking-wide">Wave Pay Details</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-yellow-500">
                              <div className="flex items-center mb-1">
                                <svg className="w-3.5 h-3.5 text-yellow-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Account Name</span>
                              </div>
                              <p className="text-sm text-gray-900 font-bold ml-5">Aung Thu</p>
                            </div>
                            <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-yellow-600">
                              <div className="flex items-center mb-1">
                                <svg className="w-3.5 h-3.5 text-yellow-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                                </svg>
                                <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Wave Pay Number</span>
                              </div>
                              <p className="text-sm text-gray-900 font-bold ml-5">09975541930</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* CB Pay Details */}
                      {showPaymentQR === 'cbpay' && (
                        <div className="bg-gradient-to-br from-[#e8ebf9] to-[#d4ddf7] border-2 border-[#a8b5e8] rounded-xl p-4 shadow-sm">
                          <div className="flex items-center justify-center mb-3">
                            <div className="bg-gradient-to-r from-[#2b3b96] to-[#232f7a] text-white px-3 py-1 rounded-full shadow-md">
                              <svg className="w-3 h-3 inline mr-1.5 -mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                              </svg>
                              <span className="text-xs font-bold uppercase tracking-wide">BANKING DETAILS</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-[#4a5bb8]">
                              <div className="flex items-center mb-1">
                                <svg className="w-3.5 h-3.5 text-[#2b3b96] mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Account Name</span>
                              </div>
                              <p className="text-sm text-gray-900 font-bold ml-5">Aung Thu</p>
                            </div>
                            <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-[#2b3b96]">
                              <div className="flex items-center mb-1">
                                <svg className="w-3.5 h-3.5 text-[#2b3b96] mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                                  <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Account Number</span>
                              </div>
                              <p className="text-sm text-gray-900 font-bold ml-5 font-mono">0003100900010577</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* AYA Pay Details */}
                      {showPaymentQR === 'ayapay' && (
                        <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200 rounded-xl p-4 shadow-sm">
                          <div className="flex items-center justify-center mb-3">
                            <div className="bg-gradient-to-r from-red-600 to-red-700 text-white px-3 py-1 rounded-full shadow-md">
                              <svg className="w-3 h-3 inline mr-1.5 -mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                              </svg>
                              <span className="text-xs font-bold uppercase tracking-wide">BANKING DETAILS</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-red-500">
                              <div className="flex items-center mb-1">
                                <svg className="w-3.5 h-3.5 text-red-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Account Name</span>
                              </div>
                              <p className="text-sm text-gray-900 font-bold ml-5">Aung Thu</p>
                            </div>
                            <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-red-600">
                              <div className="flex items-center mb-1">
                                <svg className="w-3.5 h-3.5 text-red-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                                  <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Account Number</span>
                              </div>
                              <p className="text-sm text-gray-900 font-bold ml-5 font-mono">40036552830</p>
                            </div>
                            <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-red-600">
                              <div className="flex items-center mb-1">
                                <svg className="w-3.5 h-3.5 text-red-600 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                                </svg>
                                <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">AYA Pay Number</span>
                              </div>
                              <p className="text-sm text-gray-900 font-bold ml-5">09975541930</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* UAB Pay Details */}
                      {showPaymentQR === 'uabpay' && (
                        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-[#2596be] rounded-xl p-4 shadow-sm">
                          <div className="flex items-center justify-center mb-3">
                            <div className="bg-gradient-to-r from-[#2596be] to-[#1e7a9e] text-white px-3 py-1 rounded-full shadow-md">
                              <svg className="w-3 h-3 inline mr-1.5 -mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                                <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                              </svg>
                              <span className="text-xs font-bold uppercase tracking-wide">BANKING DETAILS</span>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-[#2596be]">
                              <div className="flex items-center mb-1">
                                <svg className="w-3.5 h-3.5 text-[#2596be] mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Account Name</span>
                              </div>
                              <p className="text-sm text-gray-900 font-bold ml-5">Aung Thu</p>
                            </div>
                            <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-[#1e7a9e]">
                              <div className="flex items-center mb-1">
                                <svg className="w-3.5 h-3.5 text-[#1e7a9e] mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z"/>
                                  <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd"/>
                                </svg>
                                <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">Account Number</span>
                              </div>
                              <p className="text-sm text-gray-900 font-bold ml-5 font-mono">010100100070341</p>
                            </div>
                            <div className="bg-white rounded-lg p-3 shadow-sm border-l-4 border-[#1e7a9e]">
                              <div className="flex items-center mb-1">
                                <svg className="w-3.5 h-3.5 text-[#1e7a9e] mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                                </svg>
                                <span className="text-xs text-gray-500 font-bold uppercase tracking-wide">UAB Pay Number</span>
                              </div>
                              <p className="text-sm text-gray-900 font-bold ml-5">09975541930</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Instructions Section - Full Width Below */}
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">How to pay:</h4>
                    <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <div className={`w-7 h-7 ${
                        showPaymentQR === 'kpay' 
                          ? 'bg-blue-100 text-blue-600' 
                          : showPaymentQR === 'ayapay'
                          ? 'bg-red-100 text-red-600'
                          : showPaymentQR === 'cbpay'
                          ? 'bg-[#e8ebf9] text-[#2b3b96]'
                          : showPaymentQR === 'wavepay'
                          ? 'bg-yellow-100 text-yellow-600'
                          : 'bg-[#d4ebf7] text-[#2596be]'
                      } rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5`}>1</div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Open {showPaymentQR === 'kpay' ? 'KPay' : showPaymentQR === 'ayapay' ? 'AYA Pay' : showPaymentQR === 'cbpay' ? 'CB Pay' : showPaymentQR === 'wavepay' ? 'Wave Pay' : 'UAB Pay'} app
                        </p>
                        <p className="text-xs text-gray-500">Launch the mobile app on your phone</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className={`w-7 h-7 ${
                        showPaymentQR === 'kpay' 
                          ? 'bg-blue-100 text-blue-600' 
                          : showPaymentQR === 'ayapay'
                          ? 'bg-red-100 text-red-600'
                          : showPaymentQR === 'cbpay'
                          ? 'bg-[#e8ebf9] text-[#2b3b96]'
                          : showPaymentQR === 'wavepay'
                          ? 'bg-yellow-100 text-yellow-600'
                          : 'bg-[#d4ebf7] text-[#2596be]'
                      } rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5`}>2</div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Scan QR code</p>
                        <p className="text-xs text-gray-500">Use the scan feature to read the QR code</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className={`w-7 h-7 ${
                        showPaymentQR === 'kpay' 
                          ? 'bg-blue-100 text-blue-600' 
                          : showPaymentQR === 'ayapay'
                          ? 'bg-red-100 text-red-600'
                          : showPaymentQR === 'cbpay'
                          ? 'bg-[#e8ebf9] text-[#2b3b96]'
                          : showPaymentQR === 'wavepay'
                          ? 'bg-yellow-100 text-yellow-600'
                          : 'bg-[#d4ebf7] text-[#2596be]'
                      } rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5`}>3</div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Complete payment</p>
                        <p className="text-xs text-gray-500">Confirm the amount and authorize payment</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <div className={`w-7 h-7 ${
                        showPaymentQR === 'kpay' 
                          ? 'bg-blue-100 text-blue-600' 
                          : showPaymentQR === 'ayapay'
                          ? 'bg-red-100 text-red-600'
                          : showPaymentQR === 'cbpay'
                          ? 'bg-[#e8ebf9] text-[#2b3b96]'
                          : showPaymentQR === 'wavepay'
                          ? 'bg-yellow-100 text-yellow-600'
                          : 'bg-[#d4ebf7] text-[#2596be]'
                      } rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5`}>4</div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Upload screenshot</p>
                        <p className="text-xs text-gray-500">Take a screenshot and upload it above</p>
                      </div>
                    </div>
                  </div>
                  </div>
                </>
              )}
              
              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowPaymentQR(null)}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => setShowPaymentQR(null)}
                  className={`flex-1 ${
                    showPaymentQR === 'kpay'
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                      : showPaymentQR === 'ayapay'
                      ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800'
                      : showPaymentQR === 'cbpay'
                      ? 'bg-gradient-to-r from-[#2b3b96] to-[#232f7a] hover:from-[#232f7a] hover:to-[#1b2560]'
                      : showPaymentQR === 'wavepay'
                      ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700'
                      : showPaymentQR === 'ok'
                      ? 'bg-gradient-to-r from-[#f8cf3f] to-[#e6bd2d] hover:from-[#e6bd2d] hover:to-[#d4ab1b]'
                      : showPaymentQR === 'yoma'
                      ? 'bg-gradient-to-r from-[#f30023] to-[#d10020] hover:from-[#d10020] hover:to-[#b0001c]'
                      : 'bg-gradient-to-r from-[#2596be] to-[#1e7a9e] hover:from-[#1e7a9e] hover:to-[#176a8a]'
                  } text-white py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02]`}
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
